import { ORDERS_BLOTTER_PREFIX } from '@src/containers/Blotters/Orders/tokens';
import {
  MixpanelEvent,
  MixpanelEventSource,
  MixpanelSourceProvider,
  tabLabelerEnumerated,
  useMixpanel,
} from '@talos/kyoko';
import { memo, useCallback, useMemo } from 'react';
import type { OrderTabProps } from '../../../../types/LayoutConfig';
import { MY_ORDERS_TAB_ID, OPEN_ORDERS_TAB_ID, useDefaultOrderTabs } from '../../../Blotters/Orders/defaults';
import { useCurrentUserOpenOrdersCount, useOpenOrdersCount } from '../../../Blotters/Orders/useOpenOrdersCount';
import {
  MobileBlotterPanel,
  type MobileBlotterPanelAddTabOption,
  type MobileBlotterPanelProps,
  type MobileBlotterPanelRenderTabArgs,
} from './MobileBlotterPanel';
import { MobileFilteredOrders } from './Orders/MobileFilteredOrders';
import { MobileOpenOrders } from './Orders/MobileOpenOrders';

/**
 * Mobile orders panel. Resolves the active tab and dispatches to either
 * `MobileOpenOrders` (shared `useOrders()` subscription) or `MobileFilteredOrders`
 * (per-tab WebSocket subscription).
 *
 * The `blotterID` matches the desktop convention (`principal/orders/<tabId>`)
 * so persisted column layout / filter state is shared across desktop and mobile.
 */
export const MobileOrdersBlotter = memo(function MobileOrdersBlotter(
  props: Omit<MobileBlotterPanelProps<OrderTabProps>, 'defaultTabs' | 'renderTab' | 'getTabIndicatorCount'>
) {
  const mixpanel = useMixpanel(MixpanelEventSource.OrdersBlotter);
  const { tabs, openOrdersDefaultFilter, newTabDefaults } = useDefaultOrderTabs();
  const openOrdersCount = useOpenOrdersCount();
  const currentUserOpenOrdersCount = useCurrentUserOpenOrdersCount();
  const tabLabeler = useMemo(() => tabLabelerEnumerated<OrderTabProps>('Orders'), []);

  const renderTab = useCallback(
    ({ tab, tabId }: MobileBlotterPanelRenderTabArgs<OrderTabProps>) => {
      const isOpenTab = tabId === OPEN_ORDERS_TAB_ID;
      return isOpenTab ? (
        <MobileOpenOrders key={tabId} defaultFilter={tab.defaultFilter ?? openOrdersDefaultFilter} />
      ) : (
        <MobileFilteredOrders
          key={tabId}
          blotterID={`${ORDERS_BLOTTER_PREFIX}/${tabId}`}
          tabLabel={tab.label}
          defaultFilter={tab.defaultFilter ?? newTabDefaults.defaultFilter}
          defaultColumns={tab.defaultColumns ?? newTabDefaults.defaultColumns}
        />
      );
    },
    [newTabDefaults.defaultColumns, newTabDefaults.defaultFilter, openOrdersDefaultFilter]
  );

  const getTabIndicatorCount = useCallback<NonNullable<MobileBlotterPanelProps<OrderTabProps>['getTabIndicatorCount']>>(
    (_tab, tabId, sourceTabs) => {
      const hasMyOrdersTab = sourceTabs.some(tab => tab.id === MY_ORDERS_TAB_ID);
      if (hasMyOrdersTab) {
        return tabId === MY_ORDERS_TAB_ID ? currentUserOpenOrdersCount : undefined;
      }
      return tabId === OPEN_ORDERS_TAB_ID ? openOrdersCount : undefined;
    },
    [currentUserOpenOrdersCount, openOrdersCount]
  );

  const addTabOptions = useMemo<MobileBlotterPanelAddTabOption<OrderTabProps>[]>(
    () => [
      {
        id: 'orders',
        label: 'Orders',
        createTab: sourceTabs => {
          const newTab = { ...newTabDefaults };
          return {
            ...newTab,
            label: tabLabeler(newTab, [...sourceTabs], 'creation'),
          };
        },
        onAdd: () => mixpanel.track(MixpanelEvent.AddTab),
      },
    ],
    [mixpanel, newTabDefaults, tabLabeler]
  );

  return (
    <MixpanelSourceProvider value={MixpanelEventSource.OrdersBlotter}>
      <MobileBlotterPanel<OrderTabProps>
        {...props}
        defaultTabs={tabs}
        renderTab={renderTab}
        getTabIndicatorCount={getTabIndicatorCount}
        addTabOptions={addTabOptions}
      />
    </MixpanelSourceProvider>
  );
});
